import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { Login } from '@mui/icons-material';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #000000 0%, #1A1A1A 100%)', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ 
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8
      }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          {/* Logo */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '3rem', md: '4.5rem' },
                fontWeight: 700,
                color: '#FFD700',
                mb: 1,
                letterSpacing: '0.02em'
              }}
            >
              Maverick
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 700,
                color: '#FFFFFF',
                letterSpacing: '0.02em'
              }}
            >
              Capital
            </Typography>
          </Box>

          {/* Tagline */}
          <Typography 
            variant="h3" 
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 500,
              color: '#FFFFFF',
              mb: 2,
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Mathematical Precision in Wealth Maximization
          </Typography>

          {/* Subtitle */}
          <Typography 
            variant="h5" 
            sx={{ 
              fontSize: { xs: '1rem', md: '1.25rem' },
              fontWeight: 400,
              color: '#E0E0E0',
              mb: 6
            }}
          >
            MaVeRicK: Model-driven Volatility Regulated Kelly Strategy
          </Typography>

          {/* Portal Access Button */}
          <Button
            variant="contained"
            size="large"
            startIcon={<Login />}
            onClick={() => navigate('/login')}
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2,
              background: '#FFD700',
              color: '#000',
              '&:hover': {
                background: '#FFED4E',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(255, 215, 0, 0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Portal Access
          </Button>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box sx={{ 
        py: 10,
        background: '#0A0A0A',
        borderTop: '1px solid #333',
        borderBottom: '1px solid #333'
      }}>
        <Container maxWidth="md">
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700',
              fontWeight: 600,
              mb: 4,
              textAlign: 'center'
            }}
          >
            Our Mission
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#E0E0E0',
              fontSize: '1.1rem',
              lineHeight: 1.8,
              mb: 4,
              textAlign: 'center'
            }}
          >
            Outperform the S&P 500 and achieve superior long-term returns by leveraging stocks and indices 
            using an adapted Kelly Criterion. We provide sophisticated investors with access to a mathematically 
            grounded approach that eliminates heuristic-based allocation strategies.
          </Typography>

          {/* Quote */}
          <Box sx={{ 
            borderLeft: '4px solid #FFD700',
            pl: 3,
            py: 2,
            my: 4,
            background: 'rgba(255, 215, 0, 0.05)'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#FFFFFF',
                fontStyle: 'italic',
                fontWeight: 400,
                mb: 1
              }}
            >
              "Compound interest is the eighth wonder of the world. He who understands it, earns it; 
              he who doesn't, pays it."
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#FFD700',
                fontWeight: 500
              }}
            >
              — Albert Einstein
            </Typography>
          </Box>
        </Container>
      </Box>

{/* Contact Section */}
      <Box sx={{ py: 10, background: '#000' }}>
        <Container maxWidth="md">
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700',
              fontWeight: 600,
              mb: 3,
              textAlign: 'center'
            }}
          >
            Private Access
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#E0E0E0',
              fontSize: '1rem',
              lineHeight: 1.6,
              mb: 4,
              textAlign: 'center'
            }}
          >
            Maverick Capital is accessible to a select group of sophisticated investors through 
            our Hong Kong-based private entity.
          </Typography>

          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2
          }}>
            <Box sx={{ 
              fontSize: '2rem'
            }}>
              ✉️
            </Box>
            <Typography 
              component="a"
              href="mailto:henri.centieiro@be-limitless.co"
              sx={{ 
                color: '#FFD700',
                fontSize: '1.1rem',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              henri.centieiro@be-limitless.co
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        py: 6,
        background: '#0A0A0A',
        borderTop: '2px solid #FFD700'
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#E0E0E0',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              mb: 2,
              textAlign: 'center'
            }}
          >
            <strong>Important Notice:</strong> This website is for informational purposes only and does not 
            constitute an offer, solicitation, or recommendation. Participation involves significant risks 
            including potential total loss of capital. Past performance is not indicative of future results. 
            Consult independent advisors before making any investment decisions.
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              fontSize: '0.85rem',
              textAlign: 'center'
            }}
          >
            © 2026 Maverick Partners Limited. Hong Kong. Private and Confidential.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
